import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  House, Folders, CalendarBlank, Receipt, SignOut, Plus, Eye, Bell, CheckCircle, Clock, Warning,
  Buildings, Fire, Wheelchair, Moon, SpeakerHigh, CaretRight
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sidebar Layout
const ClientLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { path: '/client', icon: House, label: 'Tableau de bord', exact: true },
    { path: '/client/dossiers', icon: Folders, label: 'Mes dossiers' },
    { path: '/client/reminders', icon: CalendarBlank, label: 'Rappels' },
    { path: '/client/invoices', icon: Receipt, label: 'Facturation' },
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex" data-testid="client-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-[#E5E7EB] flex flex-col">
        <div className="p-6 border-b border-[#E5E7EB]">
          <Link to="/" className="font-bold text-xl tracking-tighter font-['Outfit']">
            CARRÉ <span className="text-[#E53935]">CHR</span>
          </Link>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-[#0A0A0A] text-white'
                      : 'text-[#52525B] hover:bg-[#F4F4F5]'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={20} weight={isActive(item.path, item.exact) ? 'fill' : 'regular'} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-[#E5E7EB]">
          <div className="mb-4 px-4">
            <p className="text-sm font-medium text-[#0A0A0A]">{user?.name}</p>
            <p className="text-xs text-[#52525B]">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2 text-sm text-[#52525B] hover:text-[#0A0A0A] w-full"
            data-testid="sidebar-logout-btn"
          >
            <SignOut size={20} />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

// Dashboard
const ClientDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ dossiers: 0, reminders: 0, invoices: 0 });
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dossiersRes, remindersRes, invoicesRes] = await Promise.all([
          axios.get(`${API}/dossiers`, { withCredentials: true }),
          axios.get(`${API}/reminders`, { withCredentials: true }),
          axios.get(`${API}/invoices`, { withCredentials: true })
        ]);

        setStats({
          dossiers: dossiersRes.data.length,
          reminders: remindersRes.data.filter(r => r.status === 'pending').length,
          invoices: invoicesRes.data.filter(i => i.status === 'pending').length
        });
        setRecentDossiers(dossiersRes.data.slice(0, 5));
        setUpcomingReminders(remindersRes.data.filter(r => r.status === 'pending').slice(0, 5));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const poleIcons = {
    terrasse: Buildings,
    securite: Fire,
    accessibilite: Wheelchair,
    licence: Moon,
    sonorisation: SpeakerHigh
  };

  const statusColors = {
    en_attente: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    en_cours: 'bg-blue-100 text-blue-800 border-blue-200',
    termine: 'bg-green-100 text-green-800 border-green-200'
  };

  const statusLabels = {
    en_attente: 'En attente',
    en_cours: 'En cours',
    termine: 'Terminé'
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <p className="text-[#52525B]">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="client-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">Bonjour, {user?.name}</h1>
        <p className="text-[#52525B]">Bienvenue dans votre espace CARRÉ CHR</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#E5E7EB] mb-8">
        <div className="p-6 bg-white border-r border-[#E5E7EB]" data-testid="stat-dossiers">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Dossiers</p>
          <p className="text-4xl font-medium text-[#0A0A0A]">{stats.dossiers}</p>
        </div>
        <div className="p-6 bg-white border-r border-[#E5E7EB]" data-testid="stat-reminders">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Rappels en attente</p>
          <p className="text-4xl font-medium text-[#E53935]">{stats.reminders}</p>
        </div>
        <div className="p-6 bg-white" data-testid="stat-invoices">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Factures à payer</p>
          <p className="text-4xl font-medium text-[#0A0A0A]">{stats.invoices}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Dossiers */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="p-4 border-b border-[#E5E7EB] flex justify-between items-center">
            <h2 className="font-medium text-[#0A0A0A]">Dossiers récents</h2>
            <Link to="/client/dossiers" className="text-sm text-[#52525B] hover:text-[#0A0A0A] flex items-center gap-1">
              Voir tout <CaretRight size={16} />
            </Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {recentDossiers.length === 0 ? (
              <p className="p-4 text-[#52525B] text-sm">Aucun dossier pour le moment</p>
            ) : (
              recentDossiers.map((dossier) => {
                const Icon = poleIcons[dossier.pole] || Folders;
                return (
                  <div key={dossier.id} className="p-4 flex items-center gap-4" data-testid={`dossier-${dossier.id}`}>
                    <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center">
                      <Icon size={20} className="text-[#0A0A0A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0A0A0A] truncate">{dossier.title}</p>
                      <p className="text-xs text-[#52525B]">{dossier.address}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 border ${statusColors[dossier.status]}`}>
                      {statusLabels[dossier.status]}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Upcoming Reminders */}
        <div className="bg-white border border-[#E5E7EB]">
          <div className="p-4 border-b border-[#E5E7EB] flex justify-between items-center">
            <h2 className="font-medium text-[#0A0A0A]">Rappels à venir</h2>
            <Link to="/client/reminders" className="text-sm text-[#52525B] hover:text-[#0A0A0A] flex items-center gap-1">
              Voir tout <CaretRight size={16} />
            </Link>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {upcomingReminders.length === 0 ? (
              <p className="p-4 text-[#52525B] text-sm">Aucun rappel en attente</p>
            ) : (
              upcomingReminders.map((reminder) => (
                <div key={reminder.id} className="p-4 flex items-center gap-4" data-testid={`reminder-${reminder.id}`}>
                  <div className="w-10 h-10 bg-[#FEF2F2] flex items-center justify-center">
                    <Bell size={20} className="text-[#E53935]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#0A0A0A] truncate">{reminder.title}</p>
                    <p className="text-xs text-[#52525B]">
                      Échéance: {format(new Date(reminder.due_date), 'dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                  <Warning size={20} className="text-[#EA580C]" />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Dossiers List
const DossiersList = () => {
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newDossier, setNewDossier] = useState({
    title: '', address: '', pole: '', description: '', facility_type: '', surface_area: '', occupants: ''
  });

  useEffect(() => {
    fetchDossiers();
  }, []);

  const fetchDossiers = async () => {
    try {
      const { data } = await axios.get(`${API}/dossiers`, { withCredentials: true });
      setDossiers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dossiers`, {
        ...newDossier,
        client_id: user.id,
        surface_area: newDossier.surface_area ? parseFloat(newDossier.surface_area) : null,
        occupants: newDossier.occupants ? parseInt(newDossier.occupants) : null
      }, { withCredentials: true });
      toast.success('Dossier créé avec succès');
      setDialogOpen(false);
      setNewDossier({ title: '', address: '', pole: '', description: '', facility_type: '', surface_area: '', occupants: '' });
      fetchDossiers();
    } catch (err) {
      toast.error('Erreur lors de la création');
    }
  };

  const poleLabels = {
    terrasse: 'Terrasses',
    securite: 'Sécurité Incendie',
    accessibilite: 'Accessibilité PMR',
    licence: 'Licences de Nuit',
    sonorisation: 'Sonorisation'
  };

  const statusColors = {
    en_attente: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    en_cours: 'bg-blue-100 text-blue-800 border-blue-200',
    termine: 'bg-green-100 text-green-800 border-green-200'
  };

  return (
    <div className="p-8" data-testid="dossiers-list">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">Mes dossiers</h1>
          <p className="text-[#52525B]">Gérez vos dossiers de conformité</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0A0A0A] hover:bg-[#27272A] rounded-none" data-testid="create-dossier-btn">
              <Plus size={20} className="mr-2" /> Nouveau dossier
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer un nouveau dossier</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4" data-testid="create-dossier-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Pôle *</label>
                <Select value={newDossier.pole} onValueChange={(val) => setNewDossier({...newDossier, pole: val})}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Sélectionnez un pôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="terrasse">Terrasses</SelectItem>
                    <SelectItem value="securite">Sécurité Incendie</SelectItem>
                    <SelectItem value="accessibilite">Accessibilité PMR</SelectItem>
                    <SelectItem value="licence">Licences de Nuit</SelectItem>
                    <SelectItem value="sonorisation">Sonorisation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Titre *</label>
                <Input value={newDossier.title} onChange={(e) => setNewDossier({...newDossier, title: e.target.value})} required className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Adresse *</label>
                <Input value={newDossier.address} onChange={(e) => setNewDossier({...newDossier, address: e.target.value})} required className="rounded-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Type établissement</label>
                  <Input value={newDossier.facility_type} onChange={(e) => setNewDossier({...newDossier, facility_type: e.target.value})} className="rounded-none" placeholder="Restaurant, Bar..." />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Surface (m²)</label>
                  <Input type="number" value={newDossier.surface_area} onChange={(e) => setNewDossier({...newDossier, surface_area: e.target.value})} className="rounded-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Capacité d'accueil</label>
                <Input type="number" value={newDossier.occupants} onChange={(e) => setNewDossier({...newDossier, occupants: e.target.value})} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Description</label>
                <Textarea value={newDossier.description} onChange={(e) => setNewDossier({...newDossier, description: e.target.value})} className="rounded-none" rows={3} />
              </div>
              <Button type="submit" className="w-full bg-[#0A0A0A] hover:bg-[#27272A] rounded-none" data-testid="submit-dossier-btn">
                Créer le dossier
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-[#52525B]">Chargement...</p>
      ) : dossiers.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Folders size={48} className="text-[#E5E7EB] mx-auto mb-4" />
          <p className="text-[#52525B] mb-4">Aucun dossier pour le moment</p>
          <Button onClick={() => setDialogOpen(true)} className="bg-[#0A0A0A] rounded-none">
            Créer mon premier dossier
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Dossier</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Pôle</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Statut</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Date</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {dossiers.map((dossier) => (
                <tr key={dossier.id} className="hover:bg-[#F4F4F5]" data-testid={`dossier-row-${dossier.id}`}>
                  <td className="p-4">
                    <p className="font-medium text-[#0A0A0A]">{dossier.title}</p>
                    <p className="text-sm text-[#52525B]">{dossier.address}</p>
                  </td>
                  <td className="p-4 text-sm text-[#52525B]">{poleLabels[dossier.pole]}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 border ${statusColors[dossier.status]}`}>
                      {dossier.status === 'en_attente' ? 'En attente' : dossier.status === 'en_cours' ? 'En cours' : 'Terminé'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-[#52525B]">
                    {format(new Date(dossier.created_at), 'dd/MM/yyyy')}
                  </td>
                  <td className="p-4">
                    <Link to={`/client/dossiers/${dossier.id}`}>
                      <Button variant="ghost" size="sm" className="rounded-none">
                        <Eye size={16} />
                      </Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Reminders
const RemindersList = () => {
  const { user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: '', reminder_type: '', due_date: null, description: '', recurrence: ''
  });

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    try {
      const { data } = await axios.get(`${API}/reminders`, { withCredentials: true });
      setReminders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reminders`, {
        ...newReminder,
        client_id: user.id,
        due_date: newReminder.due_date ? format(newReminder.due_date, 'yyyy-MM-dd') : null
      }, { withCredentials: true });
      toast.success('Rappel créé avec succès');
      setDialogOpen(false);
      setNewReminder({ title: '', reminder_type: '', due_date: null, description: '', recurrence: '' });
      fetchReminders();
    } catch (err) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.put(`${API}/reminders/${id}/complete`, {}, { withCredentials: true });
      toast.success('Rappel marqué comme terminé');
      fetchReminders();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const typeLabels = {
    extincteur: 'Vérification extincteur',
    terrasse_estivale: 'Dépôt terrasse estivale',
    electrique: 'Vérification électrique',
    levage: 'Contrôle levage',
    hotte: 'Entretien hotte'
  };

  return (
    <div className="p-8" data-testid="reminders-list">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">Rappels</h1>
          <p className="text-[#52525B]">Gérez vos échéances de conformité</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0A0A0A] hover:bg-[#27272A] rounded-none" data-testid="create-reminder-btn">
              <Plus size={20} className="mr-2" /> Nouveau rappel
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle>Créer un rappel</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4" data-testid="create-reminder-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Type *</label>
                <Select value={newReminder.reminder_type} onValueChange={(val) => setNewReminder({...newReminder, reminder_type: val})}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Type de rappel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="extincteur">Vérification extincteur</SelectItem>
                    <SelectItem value="terrasse_estivale">Dépôt terrasse estivale</SelectItem>
                    <SelectItem value="electrique">Vérification électrique</SelectItem>
                    <SelectItem value="levage">Contrôle levage</SelectItem>
                    <SelectItem value="hotte">Entretien hotte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Titre *</label>
                <Input value={newReminder.title} onChange={(e) => setNewReminder({...newReminder, title: e.target.value})} required className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Date d'échéance *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left rounded-none border-[#E5E7EB]">
                      <CalendarBlank className="mr-2" size={16} />
                      {newReminder.due_date ? format(newReminder.due_date, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newReminder.due_date}
                      onSelect={(date) => setNewReminder({...newReminder, due_date: date})}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Récurrence</label>
                <Select value={newReminder.recurrence} onValueChange={(val) => setNewReminder({...newReminder, recurrence: val})}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue placeholder="Pas de récurrence" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Pas de récurrence</SelectItem>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="quarterly">Trimestriel</SelectItem>
                    <SelectItem value="yearly">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Description</label>
                <Textarea value={newReminder.description} onChange={(e) => setNewReminder({...newReminder, description: e.target.value})} className="rounded-none" rows={2} />
              </div>
              <Button type="submit" className="w-full bg-[#0A0A0A] hover:bg-[#27272A] rounded-none" data-testid="submit-reminder-btn">
                Créer le rappel
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : reminders.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Bell size={48} className="text-[#E5E7EB] mx-auto mb-4" />
          <p className="text-[#52525B] mb-4">Aucun rappel configuré</p>
          <Button onClick={() => setDialogOpen(true)} className="bg-[#0A0A0A] rounded-none">
            Créer mon premier rappel
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className={`bg-white border border-[#E5E7EB] p-4 flex items-center gap-4 ${reminder.status === 'completed' ? 'opacity-60' : ''}`}
              data-testid={`reminder-item-${reminder.id}`}
            >
              <div className={`w-12 h-12 flex items-center justify-center ${reminder.status === 'completed' ? 'bg-green-100' : 'bg-[#FEF2F2]'}`}>
                {reminder.status === 'completed' ? (
                  <CheckCircle size={24} className="text-green-600" weight="fill" />
                ) : (
                  <Clock size={24} className="text-[#E53935]" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#0A0A0A]">{reminder.title}</p>
                <p className="text-sm text-[#52525B]">{typeLabels[reminder.reminder_type] || reminder.reminder_type}</p>
                <p className="text-xs text-[#52525B]">
                  Échéance: {format(new Date(reminder.due_date), 'dd MMMM yyyy', { locale: fr })}
                  {reminder.recurrence && ` • Récurrence: ${reminder.recurrence}`}
                </p>
              </div>
              {reminder.status !== 'completed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleComplete(reminder.id)}
                  className="rounded-none"
                  data-testid={`complete-reminder-${reminder.id}`}
                >
                  <CheckCircle size={16} className="mr-2" /> Terminer
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Invoices
const InvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const { data } = await axios.get(`${API}/invoices`, { withCredentials: true });
        setInvoices(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);

  const handlePay = async (id) => {
    try {
      await axios.put(`${API}/invoices/${id}/pay`, {}, { withCredentials: true });
      toast.success('Facture marquée comme payée');
      setInvoices(invoices.map(inv => inv.id === id ? { ...inv, status: 'paid' } : inv));
    } catch (err) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="p-8" data-testid="invoices-list">
      <div className="mb-8">
        <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A]">Facturation</h1>
        <p className="text-[#52525B]">Consultez vos factures</p>
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : invoices.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] p-12 text-center">
          <Receipt size={48} className="text-[#E5E7EB] mx-auto mb-4" />
          <p className="text-[#52525B]">Aucune facture pour le moment</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB]">
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">N° Facture</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Description</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Montant</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Échéance</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">Statut</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-[#F4F4F5]" data-testid={`invoice-row-${invoice.id}`}>
                  <td className="p-4 font-medium">{invoice.invoice_number}</td>
                  <td className="p-4 text-sm text-[#52525B]">{invoice.description}</td>
                  <td className="p-4 font-medium">{invoice.amount.toLocaleString('fr-FR')} €</td>
                  <td className="p-4 text-sm text-[#52525B]">{format(new Date(invoice.due_date), 'dd/MM/yyyy')}</td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 border ${invoice.status === 'paid' ? 'bg-green-100 text-green-800 border-green-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}`}>
                      {invoice.status === 'paid' ? 'Payée' : 'En attente'}
                    </span>
                  </td>
                  <td className="p-4">
                    {invoice.status !== 'paid' && (
                      <Button
                        size="sm"
                        onClick={() => handlePay(invoice.id)}
                        className="bg-[#16A34A] hover:bg-[#15803D] rounded-none"
                        data-testid={`pay-invoice-${invoice.id}`}
                      >
                        Marquer payée
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { ClientLayout, ClientDashboard, DossiersList, RemindersList, InvoicesList };
